import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import {
  useDaily,
  useVideoTrack,
  useAudioTrack,
  DailyVideo,
  useLocalSessionId,
} from '@daily-co/daily-react';

import { useInspectorStyles } from './styles';

import VideoPlaceholder from '../../components/VideoPlaceholder';
import SettingsMenu from '../../components/SettingsMenu';
import NetworkStats from '../../components/NetworkStats';
import Typography from '@mui/material/Typography';
import MicOffIcon from '../../icons/MicOffIcon';
import MicOnIcon from '../../icons/MicOnIcon';
import VideoOffIcon from '../../icons/VideoOffIcon';
import VideoOnIcon from '../../icons/VideoOnIcon';
import { DailyParticipant } from '@daily-co/daily-js';

const NetworkStatsComponent = () => {
  const callObject = useDaily();
  const [networkStats, setNetworkStats] = useState({
    quality: 0,
    send: 0,
    recv: 0,
    loss: 0,
  });

  useEffect(() => {
    const getNetworkStats = async () => {
      if (!callObject) return;
      try {
        const stats = await callObject.getNetworkStats();
        if (!stats) return;

        setNetworkStats({
          quality: stats.quality || 0,
          send: stats.stats?.latest?.sendBitsPerSecond || 0,
          recv: stats.stats?.latest?.recvBitsPerSecond || 0,
          loss: stats.stats?.latest?.totalRecvPacketLoss || 0,
        });
      } catch (err) {
        console.error('Error getting network stats:', err);
      }
    };

    const interval = setInterval(getNetworkStats, 3000);
    getNetworkStats(); // Initial call

    return () => clearInterval(interval);
  }, [callObject]);

  return (
    <NetworkStats
      quality={Number(networkStats.quality)}
      recv={networkStats.recv}
      send={networkStats.send}
      onHoverIcon={true}
    />
  );
};

interface LocalParticipantProps {
  participant: DailyParticipant | undefined;
}

const LocalParticipant = ({ participant }: LocalParticipantProps) => {
  const styles = useInspectorStyles();
  const callObject = useDaily();
  const localSessionId: any = useLocalSessionId();
  const localVideo = useVideoTrack(localSessionId);
  const localAudio = useAudioTrack(localSessionId);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  /*
  const { updateInputSettings, errorMsg, inputSettings } = useInputSettings({
    onError: useCallback(() => {
      setErrorMessage(errorMsg);
    }, []),

    onInputSettingsUpdated: useCallback(
      (event: DailyEventObjectInputSettingsUpdated) => {
        console.log(
          'Input settings updated:',
          event.inputSettings?.video?.processor
        );
      },
      []
    ),
  })

  useEffect(() => {
    //console.log('inputSettings', inputSettings)

  }, [inputSettings])

  const enableBackgroundBlur = () => {
    updateInputSettings({
      video: {
        processor: {
          type: 'background-blur',
          config: { strength: 0.5 },
        },
      },
    })
  }

  useEffect(() => {
  //  enableBackgroundBlur()
  }, [localVideoRef])
*/
  const toggleVideo = useCallback(() => {
    callObject?.setLocalVideo(localVideo.isOff);
  }, [callObject, localVideo.isOff]);

  const toggleAudio = useCallback(() => {
    callObject?.setLocalAudio(localAudio.isOff);
  }, [callObject, localAudio.isOff]);

  const videoTrackStyle = styles.videoTrack as React.CSSProperties;
  const videoComponentMemo = useMemo(
    () => (
      <DailyVideo
        type="video"
        sessionId={localSessionId}
        mirror
        ref={localVideoRef}
        style={{
          ...videoTrackStyle,
          objectFit: 'cover',
        }}
      />
    ),
    [localSessionId],
  );

  console.log('::::participant?.user_name :', participant?.user_name);

  return (
    <Box sx={styles.localPreview}>
      <Box sx={styles.localVideoTrackIconsWrapper}>
        <Box sx={styles.localVideoTrackIcon}>
          <IconButton size="small" onClick={toggleAudio}>
            {!localAudio.isOff ? (
              <MicOnIcon sx={{ strokeWidth: 2 }} />
            ) : (
              <MicOffIcon sx={{ strokeWidth: 2 }} />
            )}
          </IconButton>
        </Box>
        <Box sx={styles.localVideoTrackIcon}>
          <IconButton size="small" onClick={toggleVideo}>
            {!localVideo.isOff ? (
              <VideoOnIcon sx={{ strokeWidth: 2 }} />
            ) : (
              <VideoOffIcon sx={{ strokeWidth: 2 }} />
            )}
          </IconButton>
        </Box>
        <Box
          sx={{ ...styles.localVideoTrackIcon, ...styles.localSettingsIcon }}>
          <SettingsMenu />
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.localVideoTrack,
          opacity: localVideo?.track?.enabled ? 1 : 0,
        }}>
        <NetworkStatsComponent />
        {videoComponentMemo}
      </Box>

      <VideoPlaceholder
        participant={participant}
        outerLayerSize={110}
        innerLayerSize={70}
        isVisible={localVideo.isOff}
      />

      <Typography sx={styles.name}>{participant?.user_name || ''}</Typography>
    </Box>
  );
};

export default LocalParticipant;
